const DOCUMENT_MIME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/csv",
  "text/plain",
]);

const DOCUMENT_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "csv",
  "ppt",
  "pptx",
  "txt",
]);

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp"]);
const VIDEO_EXTENSIONS = new Set(["mp4", "avi", "mov", "webm"]);
const AUDIO_EXTENSIONS = new Set(["mp3", "wav", "ogg", "m4a", "webm"]);

function getFileExtension(fileName) {
  if (!fileName || typeof fileName !== "string") {
    return "";
  }
  const parts = fileName.split(".");
  if (parts.length < 2) {
    return "";
  }
  return parts.pop().toLowerCase();
}

/**
 * Classify a chat attachment for message_type routing.
 * @returns {"image" | "gif" | "video" | "audio" | "document" | "other"}
 */
export function classifyChatFile(file) {
  const mime = (file?.type || "").toLowerCase();
  const ext = getFileExtension(file?.name);

  if (mime === "image/gif" || ext === "gif") {
    return "gif";
  }
  if (mime.startsWith("image/") || IMAGE_EXTENSIONS.has(ext)) {
    return "image";
  }
  if (mime.startsWith("video/") || VIDEO_EXTENSIONS.has(ext)) {
    return "video";
  }
  if (mime.startsWith("audio/") || AUDIO_EXTENSIONS.has(ext)) {
    return "audio";
  }
  if (DOCUMENT_MIME_TYPES.has(mime) || DOCUMENT_EXTENSIONS.has(ext)) {
    return "document";
  }

  return "other";
}

/**
 * Full-color send class when the composer has text or any queued attachment.
 * Images/videos live in `mediaCollection`; PDFs, docs, and other files live in `files`.
 */
export function getChatSendButtonClass({
  message,
  files = [],
  mediaCollection = [],
} = {}) {
  const hasText = Boolean(message && String(message).length > 0);
  const hasFiles = Array.isArray(files) && files.length > 0;
  const hasMedia = Array.isArray(mediaCollection) && mediaCollection.length > 0;

  return hasText || hasFiles || hasMedia ? "chatSend isInput" : "chatSend";
}

/**
 * PDF and Word documents use the thumbnail upload pipeline.
 */
export function shouldUsePdfThumbnailUpload(file) {
  const mime = (file?.type || "").toLowerCase();
  const ext = getFileExtension(file?.name);

  return (
    mime === "application/pdf" ||
    mime === "application/msword" ||
    mime ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    ext === "pdf" ||
    ext === "doc" ||
    ext === "docx"
  );
}

/**
 * Personalize group name for the current viewer: replace their display name with "You".
 * Whole-token match only; used only for display (e.g. chat header). Not persisted.
 *
 * @param {string} groupName - Stored group title (e.g. "John, Sarah, Mike")
 * @param {string} receiverDisplayName - Current user's display name
 * @returns {string} Personalized title (e.g. "You, John, Mike") or unchanged groupName if no match
 */
export function personalizeGroupNameForReceiver(
  groupName,
  receiverDisplayName,
) {
  if (
    groupName == null ||
    (typeof groupName === "string" && groupName.trim() === "")
  ) {
    return typeof groupName === "string" ? groupName : "";
  }
  if (receiverDisplayName == null || typeof receiverDisplayName !== "string") {
    return groupName;
  }
  const receiverTrimmed = receiverDisplayName.trim();
  if (receiverTrimmed === "") {
    return groupName;
  }
  // Tokenize: split only on comma/semicolon so multi-word names (e.g. "Shubham Somani") stay one token
  const tokens = groupName
    .split(/\s*[,;]\s*/)
    .map((t) => t.trim())
    .filter((t) => t !== "");
  const remainingTokens = tokens.filter((t) => t !== receiverTrimmed);
  // Only add "You" if the receiver's name was actually in the group name
  if (remainingTokens.length === tokens.length) {
    return groupName;
  }
  if (remainingTokens.length === 0) {
    return "You";
  }
  return "You, " + remainingTokens.join(", ");
}
